import { useEffect, useRef } from 'react'

/**
 * Scroll-linked reveal: animation progress is driven by scroll position.
 * As the element enters the viewport, opacity/translateY/scale/blur
 * track scroll in real time via rAF. Once fully visible, switches to
 * CSS class and stops listening.
 * Respects prefers-reduced-motion.
 */
export function useRevealOnScroll<T extends HTMLElement = HTMLDivElement>(
  delay = 0,
  _threshold = 0.2, // kept for API compat, unused
) {
  const ref = useRef<T>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    // Reduced motion — show instantly
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.classList.add('is-visible')
      return
    }

    let rafId = 0
    let done = false

    const compute = () => {
      if (done || !el) return
      const rect = el.getBoundingClientRect()
      const vh = window.innerHeight
      // progress 0→1: starts when element bottom crosses viewport bottom,
      // completes when element top reaches 45% down the viewport
      const raw = (vh - rect.top) / (vh * 0.52)
      const progress = Math.min(1, Math.max(0, raw))

      if (progress >= 1) {
        // Hand off to CSS — remove inline overrides
        el.style.removeProperty('opacity')
        el.style.removeProperty('transform')
        el.style.removeProperty('filter')
        el.classList.add('is-visible')
        done = true
        window.removeEventListener('scroll', onScroll, {
          capture: false,
        } as EventListenerOptions)
        return
      }

      const isMobile = window.innerWidth < 768
      const ty = (1 - progress) * (isMobile ? 10 : 18)
      const sc = 0.97 + 0.03 * progress
      const bl = isMobile ? 0 : (1 - progress) * 3

      el.style.opacity = String(Math.round(progress * 1000) / 1000)
      el.style.transform = `translateY(${ty.toFixed(2)}px) scale(${sc.toFixed(4)})`
      el.style.filter = bl > 0.05 ? `blur(${bl.toFixed(2)}px)` : 'none'
    }

    const onScroll = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(compute)
    }

    // Apply delay before starting to listen
    const timer = setTimeout(() => {
      compute() // check initial position
      window.addEventListener('scroll', onScroll, { passive: true })
    }, delay)

    return () => {
      clearTimeout(timer)
      cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', onScroll, {
        capture: false,
      } as EventListenerOptions)
    }
  }, [delay])

  return ref
}
